// Utilities
import { defineStore } from 'pinia'
import { useFirestore } from 'vuefire'
import { collection, query, onSnapshot, Unsubscribe } from 'firebase/firestore'
import { Member } from '@/models/Member'
import { logger } from '@/utils/logger'

export const useMembersStore = defineStore('members', {
    state: () => ({
        tenantId: '' as string,
        projectId: '' as string,
        tenantMembers: [] as Member[],
        projectMembers: [] as Member[],
        /**
         * The project `projectMembers` is the roster of — set when its snapshot
         * lands, so "not loaded yet" and "nobody on it" can be told apart. A
         * form that switches project (« Nouvelle tâche » on `/tasks`) clears an
         * assignee only against the new project's real roster.
         */
        projectMembersLoadedFor: '' as string,
        /** The project roster's read failed (rules, network): said by the screens that wait for it, never shown as « nobody ». */
        projectMembersFailed: false,
        projectMembersUnsub: null as Unsubscribe | null,
        tenantMembersUnsub: null as Unsubscribe | null,
    }),
    getters: {
        tenantMemberUids: (state): string[] => state.tenantMembers.map((m) => m.uid!).filter(Boolean),
        projectMemberUids: (state): string[] => state.projectMembers.map((m) => m.uid!).filter(Boolean),
    },
    actions: {
        async listTenantMembers(tenantId: string) {
            try {
                if (!tenantId) {
                    logger.error('listTenantMembers: tenantId is required')
                    return
                }
                
                if (this.tenantMembersUnsub && this.tenantId !== tenantId) {
                    this.tenantMembersUnsub()
                    this.tenantMembersUnsub = null
                }
                if (this.tenantMembersUnsub && this.tenantId === tenantId) {
                    return
                }
                
                this.tenantId = tenantId
                const db = useFirestore()
                const q = query(collection(db, `tenants/${tenantId}/members`))
                
                this.tenantMembersUnsub = onSnapshot(
                    q,
                    (snap) => {
                        this.tenantMembers = snap.docs
                            .map((d) => Member.fromFirebase(d))
                            .filter((x): x is Member => x !== null)
                    },
                    (error) => {
                        logger.error('Error in tenant members subscription', error)
                    }
                )
            } catch (error) {
                logger.error('listTenantMembers error', error)
            }
        },
        clearTenantMembers() {
            if (this.tenantMembersUnsub) {
                this.tenantMembersUnsub()
                this.tenantMembersUnsub = null
            }
            this.tenantMembers = []
            this.tenantId = ''
        },
        async listProjectMembers(projectId: string) {
            try {
                if (!projectId) {
                    logger.error('listProjectMembers: projectId is required')
                    return
                }
                
                if (this.projectMembersUnsub && this.projectId !== projectId) {
                    this.projectMembersUnsub()
                    this.projectMembersUnsub = null
                    // The previous project's people must not be offered for
                    // this one while its own roster is on its way.
                    this.projectMembers = []
                    this.projectMembersLoadedFor = ''
                    this.projectMembersFailed = false
                }
                if (this.projectMembersUnsub && this.projectId === projectId) {
                    return
                }
                
                this.projectId = projectId
                const db = useFirestore()
                const q = query(collection(db, `projects/${projectId}/members`))
                
                this.projectMembersUnsub = onSnapshot(
                    q,
                    (snap) => {
                        this.projectMembers = snap.docs
                            .map((d) => Member.fromFirebase(d))
                            .filter((x): x is Member => x !== null)
                        this.projectMembersLoadedFor = projectId
                        this.projectMembersFailed = false
                    },
                    (error) => {
                        logger.error('Error in project members subscription', error)
                        this.projectMembersFailed = true
                        // A failed listener is dead: drop it, so the next
                        // listProjectMembers (the dialog's « close and try again »)
                        // subscribes afresh instead of early-returning on it.
                        this.projectMembersUnsub?.()
                        this.projectMembersUnsub = null
                    }
                )
            } catch (error) {
                logger.error('listProjectMembers error', error)
            }
        },
        clearProjectMembers() {
            if (this.projectMembersUnsub) {
                this.projectMembersUnsub()
                this.projectMembersUnsub = null
            }
            this.projectMembers = []
            this.projectMembersLoadedFor = ''
            this.projectMembersFailed = false
            this.projectId = ''
        },
    },
})


